// =====================================================
// TDI COACHING MODAL - Detailed AI Coaching Popup
// =====================================================

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiMessageCircle, FiRefreshCw, FiZap, FiTrendingUp, FiTrendingDown, FiShield } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

interface TDICoachingModalProps {
  isOpen: boolean;
  onClose: () => void;
  tdiData: {
    tdi_score?: number;
    tier?: string;
    trend?: string;
    trend_change?: number;
    btdi?: number;
    cetdi?: number;
    components?: {
      behavior?: {
        capital_preservation_behavior?: number;
        decision_consistency?: number;
        risk_avoidance?: number;
        behavioral_stability?: number;
      };
      capital?: {
        capital_preservation_score?: number;
        growth_score?: number;
        good_skips_usd?: number;
        bad_skips_usd?: number;
      };
    };
    directional_bias?: {
      has_warning?: boolean;
      dominant_side?: string;
      long_count?: number;
      short_count?: number;
      long_wins?: number;
      short_wins?: number;
    };
    decisions_counted?: number;
    period_days?: number;
  } | null;
}

const TDICoachingModal: React.FC<TDICoachingModalProps> = ({ isOpen, onClose, tdiData }) => {
  const [coaching, setCoaching] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-generate coaching when modal opens
  useEffect(() => {
    if (isOpen && tdiData?.tdi_score && !coaching) {
      generateCoaching();
    }
  }, [isOpen, tdiData]);

  const generateCoaching = async () => {
    if (!tdiData || !tdiData.tdi_score) {
      setError('Need at least 5 decisions to generate coaching');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Calculate winrates for directional bias
      const longWinrate = tdiData.directional_bias?.long_count && tdiData.directional_bias.long_count > 0
        ? (tdiData.directional_bias.long_wins || 0) / tdiData.directional_bias.long_count
        : 0;
      const shortWinrate = tdiData.directional_bias?.short_count && tdiData.directional_bias.short_count > 0
        ? (tdiData.directional_bias.short_wins || 0) / tdiData.directional_bias.short_count
        : 0;

      const formattedData = {
        tdi_final_scaled: tdiData.tdi_score,
        tdi_tier: tdiData.tier,
        trend: tdiData.trend,
        trend_change: tdiData.trend_change,
        btdi: tdiData.btdi || 0,
        cetdi: tdiData.cetdi || 0,
        components: {
          behavior: tdiData.components?.behavior || {},
          capital: tdiData.components?.capital || {}
        },
        directional_bias: {
          has_warning: tdiData.directional_bias?.has_warning || false,
          dominant_side: tdiData.directional_bias?.dominant_side,
          long_winrate: longWinrate,
          short_winrate: shortWinrate,
          long_count: tdiData.directional_bias?.long_count || 0,
          short_count: tdiData.directional_bias?.short_count || 0
        },
        meta: {
          period_label: tdiData.period_days ? `Last ${tdiData.period_days} days` : 'Last 30 days',
          trader_profile: 'discretionary',
          decisions_counted: tdiData.decisions_counted
        }
      };

      const { data, error: fnError } = await supabase.functions.invoke('tdi-coaching', {
        body: { tdiData: formattedData }
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data?.coaching) {
        setCoaching(data.coaching);
      } else {
        throw new Error('No coaching response received');
      }
    } catch (err) {
      console.error('Error generating coaching:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate coaching');
    } finally {
      setIsLoading(false);
    }
  };

  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'EXCELLENT': return 'text-emerald-400';
      case 'GOOD': return 'text-cyan-400';
      case 'FAIR': return 'text-amber-400';
      default: return 'text-gray-400';
    }
  };

  const getTrendIcon = (trend?: string) => {
    if (trend === 'IMPROVING') return FiTrendingUp;
    if (trend === 'DECLINING') return FiTrendingDown;
    return FiShield;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-purple-500/30 shadow-2xl shadow-purple-500/10 overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-white/5 bg-gradient-to-r from-purple-600/10 to-cyan-600/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 flex items-center justify-center">
                  <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">AI Discipline Coach</h2>
                  <p className="text-sm text-gray-400">Personalized TDI Analysis</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* TDI Summary */}
            {tdiData?.tdi_score && (
              <div className="mt-4 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-cyan-400">{tdiData.tdi_score}</span>
                  <span className={`text-sm font-semibold ${getTierColor(tdiData.tier)}`}>
                    {tdiData.tier}
                  </span>
                </div>
                {tdiData.trend && tdiData.trend !== 'NEW' && (
                  <div className={`flex items-center gap-1 text-sm ${
                    tdiData.trend === 'IMPROVING' ? 'text-emerald-400' :
                    tdiData.trend === 'DECLINING' ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    <SafeIcon icon={getTrendIcon(tdiData.trend)} className="w-4 h-4" />
                    <span>{tdiData.trend_change !== undefined ? (tdiData.trend_change > 0 ? '+' : '') + tdiData.trend_change : ''}</span>
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  Based on {tdiData.decisions_counted} decisions
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Loading State */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-purple-400 animate-spin mb-4" />
                <p className="text-gray-400">Analyzing your trading discipline...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 mb-4">
                {error}
                <button
                  onClick={generateCoaching}
                  className="ml-3 text-red-300 underline hover:text-red-200"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Coaching Content */}
            {coaching && !isLoading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="prose prose-invert prose-sm max-w-none"
              >
                <div className="p-5 rounded-xl bg-gray-800/50 border border-white/5 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                  {coaching}
                </div>
              </motion.div>
            )}

            {/* No TDI Data */}
            {!tdiData?.tdi_score && !isLoading && (
              <div className="text-center py-12">
                <SafeIcon icon={FiShield} className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Need at least 5 trading decisions to generate coaching.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/5 bg-gray-900/50 flex justify-between items-center">
            <p className="text-xs text-gray-500">
              Powered by AI
            </p>
            <button
              onClick={generateCoaching}
              disabled={isLoading || !tdiData?.tdi_score}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white text-sm font-medium hover:from-purple-500 hover:to-cyan-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              {coaching ? 'Regenerate' : 'Generate Coaching'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TDICoachingModal;
